import React, { useContext, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import ParkingIcon from 'mdi-react/ParkingIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import * as listStyles from '../../../core/admin-list.styles';
import StyleConstants from '../../../core/styles.constants';
import LinkedOfficeChips from '../../../core/components/linked-office-chips/linked-office-chips.component';
import EditParkingModal from '../modals/edit-parking-modal.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import type Office from '../../offices/models/office';
import type Parking from '../models/parking';

/**
 * `row` is the flush, full-width list row (office chips ride along the right);
 * `grid` is the bordered card used by the grid view, where the chips sit underneath.
 * Same two shapes as the facilities list, so both sections read alike.
 */
export type ParkingCardVariant = 'row' | 'grid';

interface Props {
  readonly initialParking: Parking;
  readonly allOffices: Office[];
  readonly variant?: ParkingCardVariant;
  readonly onDelete: (value: Parking) => void;
  readonly handleChange: (
    originalParking: Parking,
    changedParking: Parking,
  ) => Promise<void>;
  readonly parkingIsValid: (value: Parking) => boolean;
}

const ParkingCard = (
  {
    initialParking,
    allOffices,
    variant = 'row',
    onDelete,
    handleChange,
    parkingIsValid,
  }: Props,
): React.JSX.Element => {
  const [showEditParkingModal, setShowEditParkingModal] = useState(
    false,
  );
  const [selectedParking] = useState<Parking>({
    ...initialParking,
  });
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);

  const linkedOfficeNames = useMemo(
    () => allOffices
      .filter((o) => selectedParking.officeIds.includes(o.id ?? ''))
      .map((o) => o.name),
    [allOffices, selectedParking.officeIds],
  );

  const openEditModal = (): void => {
    setShowEditParkingModal(true);
  };

  // Opening the row has to work from the keyboard too, so it behaves like the button it is.
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openEditModal();
    }
  };

  const capacityLabel = `${selectedParking.capacity} ${
    selectedParking.capacity === 1 ? t('parking-space') : t('parking-spaces')
  }`;

  // Tile + name + capacity: the part of a parking area that looks the same in both variants.
  const identity = (
    <>
      <div className={style(listStyles.rowTile(StyleConstants.ORANGE_COLOR))}>
        <ParkingIcon size={20} />
      </div>
      <div className={style(listStyles.rowWho)}>
        <span className={style(listStyles.rowName)} title={selectedParking.name}>
          {selectedParking.name}
        </span>
        <span className={style(listStyles.rowSub)}>{capacityLabel}</span>
      </div>
    </>
  );

  const chevron = (
    <span className={style(listStyles.rowGo)}>
      <ChevronRightIcon size={20} />
    </span>
  );

  return (
    <>
      {variant === 'grid' ? (
        <div
          className={style(listStyles.gridCard)}
          key={selectedParking.name}
          role="button"
          tabIndex={0}
          onClick={openEditModal}
          onKeyDown={handleKeyDown}
        >
          <div className={style(listStyles.gridCardTop)}>
            {identity}
            {chevron}
          </div>
          <LinkedOfficeChips officeNames={linkedOfficeNames} maxVisible={1} />
        </div>
      ) : (
        <div
          className={style(listStyles.flushRow)}
          key={selectedParking.name}
          role="button"
          tabIndex={0}
          onClick={openEditModal}
          onKeyDown={handleKeyDown}
        >
          {identity}
          {/* On narrow screens the row keeps only the essentials; the chips are the first to go. */}
          <div className={style(listStyles.rowLinks(isMobile))}>
            <LinkedOfficeChips officeNames={linkedOfficeNames} maxVisible={2} inline />
          </div>
          {chevron}
        </div>
      )}
      <EditParkingModal
        showModal={showEditParkingModal}
        handleClose={(): void => {
          setShowEditParkingModal(false);
        }}
        parking={selectedParking}
        allOffices={allOffices}
        handleSave={handleChange}
        onDelete={onDelete}
        parkingIsValid={parkingIsValid}
      />
    </>
  );
};

export default ParkingCard;
